<script setup lang="ts">
  import pluralize from 'pluralize';
  import { computed, onBeforeMount, ref } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import type { Email, EmailThread, Task } from '@stuut/api/models';
  import { DirectionBadge } from '@ui/components/badge';
  import { Button } from '@ui/components/button';
  import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@ui/components/collapsible';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import { EmailThreadDrawer } from '@/components/email-threads';
  import EmailBody from '@/components/emails/EmailBody.vue';
  import { useTaskStore } from '@/store';

  interface Props {
    email?: Email;
    emailContainerClass?: HTMLAttributes['class'];
    emailThread?: EmailThread;
    hideCollapsibleTrigger?: boolean;
    showReplyButton?: boolean;
    task?: Task;
  }

  const props = defineProps<Props>();
  const showAllEmails = ref(false);
  const showEmailThread = ref(false);
  const shouldOptionalReply = ref(false);
  const drawerKey = ref(0);
  const isExpanded = ref(true);
  const taskStore = useTaskStore();
  const resolvedEmail = ref<Email>();
  const resolvedEmailThread = ref<EmailThread>();

  const componentEmail = computed(() => props.email || resolvedEmail.value);
  const componentEmailThread = computed(() => props.emailThread || resolvedEmailThread.value);

  const visibleEmails = computed(() => {
    const emails = componentEmailThread.value?.emails?.filter((em) => !em.isDraft);

    if (showAllEmails.value) return emails;

    const sourceIndex = emails?.findIndex((em) => em.id === componentEmail.value?.id);

    return emails?.slice(sourceIndex);
  });

  const totalEmails = computed(
    () => componentEmailThread.value?.emails?.filter((em) => !em.isDraft)?.length || 0,
  );
  const totalVisibleEmails = computed(() => visibleEmails.value?.length || 0);
  const remainingEmails = computed(() => totalEmails.value - totalVisibleEmails.value || 0);

  const collapsedIds = ref<Record<string, boolean>>({});

  const isEmailCollapsed = (vEmail: Email): boolean => {
    const override = collapsedIds.value[vEmail.id];
    if (override !== undefined) return override;
    return vEmail.id !== componentEmail.value?.id;
  };

  const toggleCollapsed = (vEmail: Email) => {
    collapsedIds.value = {
      ...collapsedIds.value,
      [vEmail.id]: !isEmailCollapsed(vEmail),
    };
  };

  const senderName = (vEmail: Email): string => {
    if (vEmail.direction === 'inbound') {
      return componentEmailThread.value?.partner?.name || vEmail.from_email || 'Unknown';
    }
    return vEmail.author?.name || vEmail.from_email || 'Unknown';
  };

  const EXPAND_MS = 250;
  const expandDuration = `${EXPAND_MS}ms`;

  const scrollToAttachments = (e: Event, vEmail: Email) => {
    e.preventDefault();
    e.stopPropagation();

    const wasCollapsed = isEmailCollapsed(vEmail);
    if (wasCollapsed) toggleCollapsed(vEmail);

    setTimeout(
      () => {
        const el = document.getElementById(`${vEmail.id}-attachments`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
      wasCollapsed ? EXPAND_MS : 0,
    );
  };

  onBeforeMount(async () => {
    if (props.task?.email_id) {
      resolvedEmail.value = await taskStore.peekFindEmail(props.task?.email_id);
    }

    if (props.task?.thread_id) {
      resolvedEmailThread.value = await taskStore.peekFindEmailThread(props.task?.thread_id);
    }
  });
</script>

<template>
  <Collapsible v-if="totalEmails" v-model:open="isExpanded">
    <div class="space-y-4">
      <div class="grid items-center grid-cols-[auto_1fr_auto]">
        <div v-if="hideCollapsibleTrigger"></div>
        <CollapsibleTrigger v-else>
          <div class="flex items-center cursor-pointer text-muted-foreground gap-x-2.5 text-2xs">
            <i class="far fa-angle-right transition-all" :class="isExpanded && 'fa-rotate-90'" />
            <span> Emails ({{ totalVisibleEmails }}) </span>
            <slot name="title" />
          </div>
        </CollapsibleTrigger>

        <div class="text-center text-blue hover:underline text-2xs">
          <Button
            v-if="remainingEmails > 0"
            variant="link"
            size="inline"
            @click="
              () => {
                if (totalEmails) {
                  isExpanded = true;
                  showAllEmails = true;
                }
              }
            "
          >
            show {{ pluralize('more email', remainingEmails, true) }}
          </Button>
        </div>

        <Button
          v-if="componentEmailThread"
          class="text-2xs"
          size="inline"
          variant="link"
          @click="showEmailThread = true"
        >
          View thread
        </Button>
      </div>

      <CollapsibleContent>
        <div v-if="visibleEmails?.length" class="space-y-4" :class="emailContainerClass">
          <template v-for="vEmail in visibleEmails" :key="vEmail.id">
            <div
              :id="vEmail.id"
              class="border-border bg-card text-card-foreground overflow-hidden border rounded-xs"
              :class="vEmail.id !== componentEmail?.id && 'bg-muted/40'"
            >
              <div
                class="flex items-center justify-between p-4 cursor-pointer"
                @click="toggleCollapsed(vEmail)"
              >
                <div class="flex items-center gap-3.5">
                  <DirectionBadge :direction="vEmail.direction" size="xs" />

                  <div class="flex flex-col gap-y-0.5">
                    <p class="text-xs font-semibold leading-tight">{{ senderName(vEmail) }}</p>
                    <p v-if="vEmail.from_email" class="text-muted-foreground text-2xs">
                      {{ vEmail.from_email }}
                    </p>
                  </div>
                </div>

                <div class="flex flex-row items-center gap-3 shrink-0">
                  <Tooltip v-if="vEmail.attachments?.length" :open-delay="0" :close-delay="150">
                    <TooltipTrigger as-child>
                      <Button
                        class="rounded-full"
                        variant="ghost"
                        size="icon-sm"
                        @click="(e) => scrollToAttachments(e, vEmail)"
                      >
                        <i class="fas fa-paperclip text-muted-foreground text-xs!"></i>
                      </Button>
                    </TooltipTrigger>

                    <TooltipContent>{{
                      pluralize('attachment', vEmail.attachments?.length, true)
                    }}</TooltipContent>
                  </Tooltip>

                  <p class="text-muted-foreground text-2xs">
                    {{ vEmail.sent_at?.local().format('MMM D, YYYY h:mmA') }}
                  </p>

                  <div
                    v-if="showReplyButton && vEmail.id === componentEmail?.id"
                    class="border-l pl-1"
                  >
                    <Button
                      v-if="showReplyButton && vEmail.id === componentEmail?.id"
                      size="icon-xs"
                      variant="transparent"
                      :disabled="shouldOptionalReply"
                      @click.stop="
                        () => {
                          shouldOptionalReply = true;
                          drawerKey++;
                          showEmailThread = true;
                        }
                      "
                    >
                      <i class="fal fa-reply text-2xs!" />
                    </Button>
                  </div>
                </div>
              </div>

              <Transition name="email-expand">
                <div v-if="!isEmailCollapsed(vEmail)" class="email-expand grid">
                  <div class="overflow-hidden">
                    <div class="border-border border-t-2 border-dotted"></div>
                    <div class="py-4 text-xs">
                      <EmailBody :email="vEmail" size="xs" compact class="leading-normal" />
                    </div>
                  </div>
                </div>
              </Transition>
            </div>
          </template>
        </div>
      </CollapsibleContent>

      <EmailThreadDrawer
        v-if="showEmailThread && visibleEmails?.length && componentEmailThread"
        :key="drawerKey"
        v-model:open="showEmailThread"
        :thread="componentEmailThread"
        :selected-email-id="componentEmail?.id"
        :optional-reply="shouldOptionalReply"
        is-slider
        default-open
        class="h-full min-h-full"
        @close="
          () => {
            resolvedEmailThread = undefined;
            showEmailThread = false;
            shouldOptionalReply = false;
          }
        "
      />
    </div>
  </Collapsible>
</template>

<style scoped>
  .email-expand {
    grid-template-rows: 1fr;
  }

  .email-expand-enter-active,
  .email-expand-leave-active {
    transition:
      grid-template-rows v-bind(expandDuration) ease,
      opacity v-bind(expandDuration) ease;
  }

  .email-expand-enter-from,
  .email-expand-leave-to {
    grid-template-rows: 0fr;
    opacity: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    .email-expand-enter-active,
    .email-expand-leave-active {
      transition: none;
    }
  }
</style>
